import CustomEvent from "custom-event";
import arrayFrom from "array-from";

const BODY_SCROLL_FREEZE_CLASS = "bfv-scroll-freeze";
const BODY_SCROLL_FREEZE_ATTR = "data-scroll-before-freeze";

function unwrapElement($element) {
  const $parent = $element.parentNode;

  // move all children out of the element
  while ($element.firstChild) {
    $parent.insertBefore($element.firstChild, $element);
  }

  // remove the empty element
  $parent.removeChild($element);
}

function findParent(element, className) {
  let parentElement = element.parentNode;

  if (parentElement) {
    if (
      !parentElement.classList ||
      !parentElement.classList.contains(className)
    ) {
      parentElement = findParent(parentElement, className);
    }
  }

  return parentElement || null;
}

function findParentAttr(element, selector) {
  let parentElement = element.parentNode;

  if (parentElement) {
    if (!parentElement.hasAttribute(selector)) {
      parentElement = findParentAttr(parentElement, selector);
    }
  }

  return parentElement || null;
}

function findParentNode(element, node) {
  let parentElement = element.parentNode;

  if (parentElement) {
    if (parentElement !== node) {
      parentElement = findParentNode(parentElement, node);
    }
  }

  return parentElement || null;
}

function findParentNodeType(element, nodeType) {
  let parentElement = element.parentNode;

  if (parentElement) {
    if (parentElement.nodeName.toLowerCase() !== nodeType.toLowerCase()) {
      parentElement = findParentNodeType(parentElement, nodeType);
    }
  }

  return parentElement || null;
}

function getQueryParam(param) {
  const url = window.location.href;

  const _param = param.replace(/[\[\]]/g, "\\$&"); // eslint-disable-line
  const regex = new RegExp(`[?&]${_param}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);

  if (!results) {
    return null;
  }

  if (!results[2]) {
    return "";
  }
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

function getWindowSize() {
  /**
   * Returns current window size.
   * (grab the correct inner size without scrollbar)
   *
   * @return {Object} with 'width' and 'height' of window
   *  - width
   *  - height
   */

  /*
   let v = window;
   let a = 'inner';

   if (!('innerWidth' in v)) {
   a = 'client';
   v = document.documentElement || document.body;
   }

   return {
   width: v[`${a}Width`],
   height: v[`${a}Height`],
   };
   */

  const v = document.documentElement || document.body;
  return {
    width: v.clientWidth,
    height: v.clientHeight,
  };
}

function getWindowScroll() {
  /**
   * Returns current scroll position of window.
   *
   * @return {Object} with 'x' and 'y' scroll position of window
   *  - x
   *  - y
   */
  const v = window;

  return {
    x: v.pageXOffset || v.scrollX || 0,
    y: v.pageYOffset || v.scrollY || 0,
    documentHeight: document.height || document.body.offsetHeight || 0,
    documentWidth: document.width || document.body.offsetWidth || 0,
  };
}

function isNumber(number) {
  return (
    !Number.isNaN(number - 0) &&
    number !== null &&
    number !== "" &&
    number !== false
  );
}

/**
 * Polyfill for classlist.contains(), since IE11 doesn't know classList of svg elements
 * @param $element
 * @param classToCheck
 * @returns {boolean}
 */
function svgContainsClass($element, classToCheck) {
  if ($element.classList) {
    return $element.classList.contains(classToCheck);
  }

  const classList = $element.getAttribute("class") || "";
  return classList.split(" ").includes(classToCheck);
}

/**
 * Polyfill for classlist.add(), since IE11 doesn't know classList of svg elements
 * @param $element
 * @param classToAdd
 */
function svgAddClass($element, classToAdd) {
  if (!svgContainsClass($element, classToAdd)) {
    if ($element.classList) {
      $element.classList.add(classToAdd);
    }

    const classList = $element.getAttribute("class") || "";
    $element.setAttribute("class", `${classList} ${classToAdd}`);
  }
}

function isScrolledInViewport(
  element,
  windowScroll = getWindowScroll(),
  offsetFactor = 0.3,
) {
  /**
   * Checks if specific element is currently visible in viewport.
   *
   * @param {Node} element          - element that will be checked if it is visible in viewport
   * @param {Object} windowScroll   - current scroll position of window
   * @param {number} offsetFactor   - factor that will be taken into account for check
   * @return {boolean}              - true/false if element is in viewport
   *
   * offsetFactor is used if viewport should get enlarged for calculation.
   * So element will be taken as visible in 'simulated' viewport even it is not yet visible in real viewport.
   *
   * references:
   *  - https://plainjs.com/javascript/styles/get-the-position-of-an-element-relative-to-the-document-24/
   *  - https://www.kirupa.com/html5/get_element_position_using_javascript.htm
   */
  let returnValue = false;
  const windowRect = getWindowSize();
  const vpOffset = windowRect.height * offsetFactor;
  const vpTop = windowScroll.y - vpOffset;
  const vpBottom = windowScroll.y + windowRect.height + vpOffset;

  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top + windowScroll.y;

  // check if top of element is close to visible viewport borders
  if (elementTop < vpBottom && elementTop + elementRect.height > vpTop) {
    // element is in viewport
    returnValue = true;
  }

  return returnValue;
}

/**
 * Function to check if element is totally in viewport
 * @function
 * @param {Node} $element - element which will be checked if totally in viewport
 * @param {number} offsetTop - number of pixels which should get excluded from the check of height of viewport
 * @return {boolean}      - true/false if element is totally in viewport
 */
function isTotallyInViewport($element, offsetTop = 0) {
  const bounding = $element.getBoundingClientRect();
  return (
    bounding.top >= offsetTop &&
    bounding.bottom <=
      (window.innerHeight || document.documentElement.clientHeight)
  );
}

/**
 * Function to check if element is totally outside the viewport
 *
 * @param $element - element which will be checked
 * @param {number} offsetTop - number of pixels which should get excluded from the check of height of viewport
 * @returns {boolean}
 */
function isTotallyOutsideViewport($element, offsetTop = 0) {
  const bounding = $element.getBoundingClientRect();

  return (
    bounding.top >
      (window.innerHeight || document.documentElement.clientHeight) ||
    bounding.bottom < offsetTop
  );
}

function dispatchCustomEvent(eventType, payload) {
  const customEvent = new CustomEvent(eventType, { detail: payload });

  // dispatch event for project
  window.dispatchEvent(customEvent);
}

function isEdge() {
  return /Edge/.test(navigator.userAgent);
}

function isIE() {
  return /Trident/.test(navigator.userAgent);
}

function isIE11() {
  return !!window.MSInputMethodContext && !!document.documentMode;
}

function isSafari() {
  return (
    navigator.vendor &&
    navigator.vendor.indexOf("Apple") > -1 &&
    navigator.userAgent &&
    navigator.userAgent.indexOf("CriOS") === -1 &&
    navigator.userAgent.indexOf("FxiOS") === -1
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Detect if device is iPhone or not
 * @returns {boolean}
 */
function isIphone() {
  return /iPhone|iPod/i.test(window.navigator.userAgent);
}

/**
 * Detect if device is Android
 * @returns {boolean}
 */
function isAndroid() {
  return /(android)/i.test(navigator.userAgent);
}

function getSiblings(elem) {
  const siblings = [];
  let sibling = elem.parentNode.firstChild;
  for (; sibling; sibling = sibling.nextSibling) {
    if (!(sibling.nodeType !== 1 || sibling === elem)) {
      siblings.push(sibling);
    }
  }
  return siblings;
}

function getSiblingsWithClass(elem, className) {
  const siblings = [];
  let sibling = elem.nextElementSibling;
  for (; sibling; sibling = sibling.nextSibling) {
    if (
      !(sibling.nodeType !== 1 || sibling === elem) &&
      sibling.classList.contains(className)
    ) {
      siblings.push(sibling);
    }
  }
  return siblings;
}

function isTouchDevice() {
  return "ontouchstart" in window || navigator.msMaxTouchPoints;
}

/**
 * @example
 * getProp(`divae.${AWARD_OBJECT}`)
 * getProp(['divae', AWARD_OBJECT]);
 *
 * @param path - dot separated string OR array with path parts
 * @param context - Object - IS MANDATORY
 * @param defaultVal
 * @returns {*} - found value OR (defaultVal || undefined)
 */
function getProp(path, context, defaultVal) {
  const pathParts = Array.isArray(path)
    ? path
    : path.split(".").filter((i) => i.length);

  if (pathParts.length === 0 || context === undefined) {
    return context === undefined ? defaultVal : context;
  }

  return getProp(pathParts, context[pathParts.shift()], defaultVal);
}

/**
 * Source https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze
 * @param obj - any valid js object
 * @return {ReadonlyArray<any>}
 */
function deepFreeze(obj) {
  // Retrieve the property names defined on obj
  const propNames = Object.getOwnPropertyNames(obj);

  // Freeze properties before freezing self
  propNames.forEach((name) => {
    const prop = obj[name];

    // Freeze prop if it is an object
    if (typeof prop === "object" && prop !== null) {
      deepFreeze(prop);
    }
  });

  // Freeze self (no-op if already frozen)
  return Object.freeze(obj);
}

/**
 * Returns true if <img> has been already loaded
 * src: https://stackoverflow.com/questions/1977871/check-if-an-image-is-loaded-no-errors-in-javascript
 *
 * @param $img - <img> element
 * @returns {boolean}
 */
function isImgLoaded($img) {
  return $img.complete && $img.naturalHeight !== 0;
}

/**
 * Source https://davidwalsh.name/javascript-debounce-function
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 */
function debounce(func, wait, immediate) {
  let timeout;
  return (...args) => {
    const context = this;
    const later = () => {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
    }
  };
}

/**
 * Returns a function, that, as long as it is called within a specific time will not be triggered
 * The function will be called if the time between each calls is grater than the N milliseconds
 */

function throttle(fn, threshhold = 250, scope = undefined) {
  let last;
  let deferTimer;
  return (...args) => {
    const context = scope || this;

    const now = +new Date();

    if (last && now < last + threshhold) {
      // hold on to it
      clearTimeout(deferTimer);
      deferTimer = setTimeout(() => {
        last = now;
        fn.apply(context, args);
      }, threshhold);
    } else {
      last = now;
      fn.apply(context, args);
    }
  };
}

/**
 * Detect if device is iOS or not
 * https://stackoverflow.com/questions/9038625/detect-if-device-is-ios
 * @returns {boolean}
 */

/**
 * Returns scrollbar width in pixels. On mobile/mac with default settings = 0;
 * source: src: https://stackoverflow.com/questions/13382516/getting-scroll-bar-width-using-javascript#answer-13382873
 * @returns {number}
 */
function getScrollbarWidth() {
  const $outer = document.createElement("div");
  $outer.style.visibility = "hidden";
  $outer.style.width = "100px";
  $outer.style.msOverflowStyle = "scrollbar"; // needed for WinJS apps

  document.body.appendChild($outer);

  const widthNoScroll = $outer.offsetWidth;
  // force scrollbars
  $outer.style.overflow = "scroll";

  // add inner div
  const $inner = document.createElement("div");
  $inner.style.width = "100%";
  $outer.appendChild($inner);

  const widthWithScroll = $inner.offsetWidth;

  // remove divs
  $outer.parentNode.removeChild($outer);

  return widthNoScroll - widthWithScroll;
}

/**
 * calculates and returns scroll-offset regarding if given element is not totally in viewport or won't fit totally in viewport
 * - if not totally in viewport but will fit in viewport -> scroll-offset will be missing part of element
 * - if not totally in viewport and also won't fit in viewport -> scroll-offset will be distance to scroll to show element directly under header
 * @returns {number}
 */

function getScrollOffset({ $element, offset = 0 }) {
  let offsetTop = offset;
  let scrollOffset = 0;
  const $header = document.querySelector("header.header");
  const bounding = $element.getBoundingClientRect();

  if ($header) {
    offsetTop += $header.clientHeight;
  }

  // check if module is in viewport
  if (!isTotallyInViewport($element, offsetTop)) {
    if (
      bounding.height <=
      (window.innerHeight || document.documentElement.clientHeight) - offsetTop
    ) {
      if (bounding.top < offsetTop) {
        scrollOffset += bounding.top - offsetTop;
      } else {
        scrollOffset +=
          bounding.bottom - window.innerHeight ||
          document.documentElement.clientHeight;
      }
    } else {
      scrollOffset += bounding.top - offsetTop;
    }
  }
  return scrollOffset;
}

function getOffsetPosition(el) {
  const rect = el.getBoundingClientRect();
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
}

function hasClass(element, className) {
  if (!element) {
    return false;
  }
  return (element.getAttribute("class") || "")
    .split(" ")
    .some((cn) => cn === className);
}

function removeClass(element, className) {
  if (!element) {
    return;
  }

  if (
    Object.prototype.toString.call(element) === "[object NodeList]" ||
    Object.prototype.toString.call(element) === "[object Array]" ||
    Object.prototype.toString.call(element) === "[object HTMLCollection]"
  ) {
    arrayFrom(element).forEach((subelement) =>
      removeClass(subelement, className),
    );
    return;
  }

  const classes = (element.getAttribute("class") || "")
    .split(" ")
    .filter(Boolean)
    .filter((cn) => cn !== className);
  element.setAttribute("class", classes.join(" "));
}

function addClass(element, className) {
  if (!element) {
    return;
  }

  if (
    Object.prototype.toString.call(element) === "[object NodeList]" ||
    Object.prototype.toString.call(element) === "[object Array]" ||
    Object.prototype.toString.call(element) === "[object HTMLCollection]"
  ) {
    arrayFrom(element).forEach((subelement) => addClass(subelement, className));
    return;
  }

  const classes = (element.getAttribute("class") || "")
    .split(" ")
    .filter(Boolean)
    .filter((cn) => cn !== className);
  classes.push(className);
  element.setAttribute("class", classes.join(" "));
}

function toggleClass(element, className) {
  if (!element) {
    return;
  }
  if (hasClass(element, className)) {
    removeClass(element, className);
  } else {
    addClass(element, className);
  }
}

function isBodyScrollFreezed() {
  const $body = document.querySelector("body");
  return hasClass($body, BODY_SCROLL_FREEZE_CLASS);
}

/* eslint-disable */
function freezeBodyScroll() {
  const yScroll = getWindowScroll().y;

  const $body = document.querySelector("body");
  $body.setAttribute(BODY_SCROLL_FREEZE_ATTR, yScroll);
  addClass($body, BODY_SCROLL_FREEZE_CLASS);
  $body.style.top = `-${yScroll}px`;
}

function unfreezeBodyScroll() {
  const $body = document.querySelector("body");
  const lastScrollOffset = $body.getAttribute(BODY_SCROLL_FREEZE_ATTR);
  removeClass($body, BODY_SCROLL_FREEZE_CLASS);
  window.scrollTo(0, lastScrollOffset);
}

function removeIE() {
  if (!("remove" in Element.prototype)) {
    Element.prototype.remove = function () {
      if (this.parentNode) {
        this.parentNode.removeChild(this);
      }
    };
  }
}

function getMeta(metaName) {
  const metas = document.getElementsByTagName("meta");

  for (let i = 0; i < metas.length; i++) {
    if (metas[i].getAttribute("name") === metaName) {
      return metas[i].getAttribute("content");
    }
  }
  return "";
}

function validatePassword(password) {
  let result = /[A-Z]/.test(password); //must contain at least one upper case letter
  result = result && /[a-z]/.test(password); //must contain at least one lower case letter
  result = result && /\d/.test(password); //must contain at least one digit
  result = result && !/[§°]/.test(password); //may not contain §°

  result = result && password.length >= 8;
  result = result && password.length <= 30;

  return result;
}

function customEventPolyfill() {
  if (typeof window.CustomEvent === "function") {
    return false;
  }

  function CustomEvent(event, params) {
    params = params || { bubbles: false, cancelable: false, detail: null };
    var evt = document.createEvent("CustomEvent");
    evt.initCustomEvent(
      event,
      params.bubbles,
      params.cancelable,
      params.detail,
    );
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  window.CustomEvent = CustomEvent;
}

export {
  addClass,
  debounce,
  deepFreeze,
  dispatchCustomEvent,
  findParent,
  findParentAttr,
  findParentNode,
  findParentNodeType,
  getMeta,
  getOffsetPosition,
  getProp,
  getScrollbarWidth,
  getSiblings,
  getSiblingsWithClass,
  getQueryParam,
  getWindowScroll,
  getWindowSize,
  isAndroid,
  isEdge,
  isIE,
  isIE11,
  isSafari,
  isIOS,
  isIphone,
  isImgLoaded,
  isNumber,
  hasClass,
  isScrolledInViewport,
  isTotallyInViewport,
  isTotallyOutsideViewport,
  isTouchDevice,
  throttle,
  removeIE,
  removeClass,
  unwrapElement,
  getScrollOffset,
  toggleClass,
  freezeBodyScroll,
  unfreezeBodyScroll,
  isBodyScrollFreezed,
  svgContainsClass,
  svgAddClass,
  validatePassword,
  customEventPolyfill,
};

